/*
 * ap-felgen-set.js — <ap-felgen-set>: Felgen-Zubehoer „Das brauchen Sie zur Felge",
 * Stufe 1 (ohne Fahrzeug). Phase B, Paket Zubehoer (29.09.2026); Umsetzungsplan
 * Felgen-Zubehoer 2.2, 2.5, 2.6, 4.3, 4.4 (Betreiber-Freigabe 26.09.2026).
 * Vanilla ES6, keine Abhaengigkeit, geladen mit `defer` aus snippets/ap-felgen-zubehoer.
 *
 * MARKUP: snippets/ap-felgen-zubehoer.liquid — teil 'oben' ist dieses Element (Fakten,
 * Reifen-Karte, Zentrierring-Karte, Texte, Muster-Antwort, Vorlagen), teil 'hinweise' die
 * Hinweisliste daneben; beide liegen in [data-nwf-bereich] (sections/needed-with.liquid).
 *
 * DATEN — genau EINE Antwort je Seite nach dem App-Vertrag v1 (tyre-trader-eu
 * app/lib/catalog/felgen-set.ts, FelgenSetAntwort {v, status, felge, reifen, ring,
 * befestigung, hinweise, fahrzeug}):
 *   data-muster   die Muster-Antwort aus <script type="application/json" data-nwf-muster>
 *                 (nur Vorschau, settings.ap_muster_zubehoer) — keine Anfrage
 *   data-app="1"  GET data-endpoint?pid=&vid= (App-Proxy, HMAC setzt Shopify), Zeitlimit
 *   sonst         KEINE Anfrage (App-Route vor dem Promote nicht da): nur die Buybox-Menge
 *                 wandert in die Hinweise.
 * Gueltig ist nur v === 1 && status === 'ok'. 404, Zeitlimit, 'retry', fremde Version:
 * NICHTS aendern — die serverseitigen Zeilen und die Umleitung bleiben (Plan 4.5: keine
 * leere Box, kein Fehlertext). window.apFelgenSet.antwort(endpoint, pid, vid) teilt die
 * Anfrage mit anderen Bloecken derselben Seite (eine Anfrage je Felge).
 *
 * ZEILENARTEN (Plan 2.1 Nr. 2):
 *   Reifen      AUSWAHL Breite -> Querschnitt (+ Saison) -> LINK zur Groessenroute. Nie
 *               vorgewaehlt, nie eine Checkbox, nie im Warenkorb. Ziel: groessen[].ziel
 *               (alle Saisons) bzw. window.apSizeTarget({scope:'felge', …, season}); nur
 *               Kollektionspfade des Ladens (ZIEL_RE), sonst die Umleitung der App.
 *   Zentrierring AUSWAHL des Innenmasses -> KAUFZEILE in der Kaufliste (<needed-with>),
 *               NIE angehakt (§ 312a Abs. 3 BGB); „weiß ich nicht" entfernt sie wieder.
 *               Antwortet die App mit einer Ringangabe, verschwinden die bisherigen
 *               Ringzeilen ([data-nw-ring]: Zufallssieger bzw. Lueckensatz).
 *   Hinweise    Befestigung (Lochzahl x Buybox-Menge), Bundform laut Lieferant, RDKS,
 *               Hinweise der Felge als CODES -> Locale-Texte (nie durchgereichter Freitext
 *               ausser den geprueften Marken aus „Exclusiv fuer …").
 *
 * WORTLAUT: nur Locale-Texte ([data-nwf-texte], de/en), Platzhalter in eckigen Klammern,
 * alles per textContent — nie innerHTML mit Daten. Altersregel der Ab-Preise (Betreiber
 * 26./27.09.2026): ohne DOT „Fabrikneu (laut Lieferant max. 2 Jahre)"; mit DOT-Jahr
 * Kalenderjahres-Differenz <= 1 „Fabrikneu", 2–5 „Neuware", >= 6 „DOT-Ware", Jahr in der
 * Zukunft nur „DOT <Jahr>" — das Jahr steht IMMER dabei; nie „Fabrikneu" an aelterer
 * DOT-Ware.
 */
(function () {
  'use strict';

  if (!('customElements' in window)) return;
  if (customElements.get('ap-felgen-set')) return;

  /* Zeitlimit der Proxy-Anfrage: die Route selbst bricht nach 2,5 s ab (retry). */
  var FRIST_MS = 4000;

  /* Die Saison-Namen des VERTRAGS der Groessenziel-Route (App SEASON_LABEL) — unabhaengig
   * von der Sprache der Seite. */
  var SAISON_VERTRAG = { sommer: 'Sommer', winter: 'Winter', ganzjahr: 'Ganzjahr' };
  var SAISONS = ['alle', 'sommer', 'winter', 'ganzjahr'];

  /* Geschuetztes Leerzeichen (als Escape, nie als unsichtbares Zeichen im Quelltext). */
  var NBSP = '\u00a0';

  /* Die einzige Adressform, die eine Antwort tragen darf: ein Kollektionspfad des Ladens
   * (wie ERLAUBTE_ADRESSE in felgen-set.ts). */
  var ZIEL_RE = /^\/collections\/[a-z0-9][a-z0-9-]*(?:\/[a-z0-9][a-z0-9-]*)?(?:\?[A-Za-z0-9._~%=&+-]+)?$/;

  function istZahl(n) {
    return typeof n === 'number' && isFinite(n);
  }

  function gueltig(a) {
    return !!a && typeof a === 'object' && a.v === 1 && a.status === 'ok';
  }

  /* ---------------------------------------------------------------- Antwort */
  var antworten = {};

  function holeAntwort(endpoint, pid, vid) {
    var key = String(endpoint) + '|' + pid + '|' + vid;
    if (antworten[key]) return antworten[key];
    var p = new Promise(function (resolve) {
      if (typeof fetch !== 'function' || !pid || !vid) {
        resolve(null);
        return;
      }
      var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      var fertig = false;
      var timer = setTimeout(function () {
        fertig = true;
        if (ctrl) ctrl.abort();
        resolve(null);
      }, FRIST_MS);
      var url = endpoint + '?pid=' + encodeURIComponent(pid) + '&vid=' + encodeURIComponent(vid);
      fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined })
        .then(function (res) {
          return res && res.ok ? res.json() : null;
        })
        .then(function (json) {
          if (fertig) return;
          clearTimeout(timer);
          resolve(gueltig(json) ? json : null);
        })
        .catch(function () {
          if (fertig) return;
          clearTimeout(timer);
          resolve(null);
        });
    });
    // Eine Absage wird nicht gemerkt — ein spaeterer Aufruf darf erneut fragen.
    p.then(function (a) {
      if (!a) delete antworten[key];
    });
    antworten[key] = p;
    return p;
  }

  window.apFelgenSet = window.apFelgenSet || { antwort: holeAntwort, gueltig: gueltig };

  /* ------------------------------------------------------------------ Hilfen */
  function fuelle(vorlage, werte) {
    var s = String(vorlage || '');
    Object.keys(werte || {}).forEach(function (k) {
      s = s.split('[' + k + ']').join(String(werte[k]));
    });
    return s;
  }

  function el(tag, klasse, text) {
    var e = document.createElement(tag);
    if (klasse) e.className = klasse;
    if (text !== undefined && text !== null) e.textContent = String(text);
    return e;
  }

  /* Masseinheit nie allein auf der naechsten Zeile: „5–6\u00a0J", „4,5–5,5\u00a0B". */
  function mitEinheit(s) {
    return String(s).replace(/ ([JB])(?=[^A-Za-z0-9]|$)/g, NBSP + '$1');
  }

  function hauptinhalt() {
    return document.getElementById('MainContent') || document;
  }

  /* Das Mengenfeld der Buybox — dieselbe Suche wie needed-with.js (nur im Hauptinhalt,
   * das per form-Attribut gebundene Feld zuerst). null = keins da. */
  function mengenFeld() {
    var wurzel = hauptinhalt();
    var form =
      wurzel.querySelector('product-form form[action*="/cart/add"]') || wurzel.querySelector('form[action*="/cart/add"]');
    if (!form) return null;
    var id = form.getAttribute('id');
    if (id) {
      var gebunden = document.querySelector('input[name="quantity"][form="' + id + '"]');
      if (gebunden) return gebunden;
    }
    return form.querySelector('input[name="quantity"]');
  }

  /* ---------------------------------------------------------------- Element */
  class ApFelgenSet extends HTMLElement {
    connectedCallback() {
      if (this._bereit) return;
      this._bereit = true;
      this.bereich = this.closest('[data-nwf-bereich]') || this.parentElement || document;
      this.sid = this.dataset.sid || 'nwf';
      this.muster = this.hasAttribute('data-muster');
      this.t = this.leseJson('[data-nwf-texte]') || {};
      this.dezimal = typeof this.t.dezimal === 'string' && this.t.dezimal ? this.t.dezimal : ',';
      this.jahr = parseInt(this.dataset.jahr || '', 10) || new Date().getFullYear();
      this.loch = parseInt(this.dataset.loch || '', 10) || 0;
      this.umleitung = this.dataset.umleitung || '';
      this.saison = 'alle';
      this.breite = null;
      this.querschnitt = null;
      this.zielLauf = 0;

      this.onMenge = this.onMenge.bind(this);
      document.addEventListener('change', this.onMenge, true);
      document.addEventListener('input', this.onMenge, true);
      this.zeigeMengen();

      var self = this;
      var quelle = null;
      if (this.muster) {
        var m = this.leseJson('[data-nwf-muster]');
        quelle = Promise.resolve(gueltig(m) ? m : null);
      } else if (this.dataset.app === '1') {
        var api = window.apFelgenSet && typeof window.apFelgenSet.antwort === 'function' ? window.apFelgenSet.antwort : holeAntwort;
        quelle = api(this.dataset.endpoint || '/apps/tyre-search/felgen-set', this.dataset.pid, this.dataset.vid);
      }
      if (!quelle) return;
      this.bereit = Promise.resolve(quelle)
        .then(function (a) {
          if (a && self.isConnected) self.zeige(a);
          return a;
        })
        .catch(function (e) {
          // Darstellung warf: die serverseitigen Zeilen stehen weiter da.
          if (window.console && console.error) console.error(e);
          return null;
        });
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onMenge, true);
      document.removeEventListener('input', this.onMenge, true);
    }

    leseJson(sel) {
      var s = this.querySelector(sel);
      if (!s) return null;
      try {
        return JSON.parse(s.textContent || 'null');
      } catch (e) {
        return null;
      }
    }

    /* Zahl in der Anzeige: 65.06 -> „65,06", 6 -> „6" (mitKomma: „6,0" wie
     * snippets/ap-felgenbreite). */
    zahl(n, mitKomma) {
      if (!istZahl(n)) return '';
      var s = String(Math.round(n * 100) / 100);
      if (mitKomma && s.indexOf('.') === -1) s += '.0';
      return s.replace('.', this.dezimal);
    }

    anzahl(n) {
      var lang = document.documentElement.lang || 'de-DE';
      return (istZahl(n) ? n : 0).toLocaleString(lang);
    }

    /* Euro (Zahl) -> Geldschreibweise des Shops (Vor-/Nachsatz aus dem Liquid). */
    geld(euro) {
      var lang = document.documentElement.lang || 'de-DE';
      var betrag = (Math.round(euro * 100) / 100).toLocaleString(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      var vor = this.dataset.geldVor || '';
      var nach = this.dataset.geldNach || '';
      if (vor || nach) return vor + betrag + nach;
      return betrag + ' €';
    }

    text(key, werte) {
      return fuelle(this.t[key] || '', werte);
    }

    /* ------------------------------------------------------------- Aufbau */
    zeige(a) {
      this.antwort = a;
      this.felge = a.felge && typeof a.felge === 'object' ? a.felge : null;
      this.zeigeFelge();
      this.zeigeReifen(a.reifen);
      this.zeigeRing(a.ring);
      this.befestigung = a.befestigung && istZahl(a.befestigung.jeFelge) ? a.befestigung : null;
      this.zeigeBund();
      this.zeigeBefestigung(Array.isArray(a.hinweise) ? a.hinweise : []);
      this.zeigeHinweise(Array.isArray(a.hinweise) ? a.hinweise : []);
      this.zeigeMengen();
      this.setAttribute('data-nwf-geladen', '');
    }

    felgeLabel() {
      var f = this.felge;
      if (!f || !istZahl(f.j) || !istZahl(f.zoll)) return '';
      return this.zahl(f.j, true) + 'J × ' + this.zahl(f.zoll) + '″';
    }

    /* Fakten und Reifen-Titel aus der Antwort: dieselbe Felge, mit der die Groessen
     * gerechnet sind (im Muster: die Beispielfelge). */
    zeigeFelge() {
      var f = this.felge;
      if (!f) return;
      var setze = function (wurzel, art, text) {
        var ziel = wurzel.querySelector('[data-nwf-fakt="' + art + '"] [data-nwf-fakt-text]');
        if (ziel && text) ziel.textContent = text;
      };
      var label = this.felgeLabel();
      setze(this, 'felge', label);
      // Geschuetzte Leerzeichen wie snippets/ap-mass-anzeige (kein Umbruch im Mass).
      if (istZahl(f.lochzahl) && istZahl(f.lochkreis)) setze(this, 'lk', 'LK ' + f.lochzahl + NBSP + '×' + NBSP + this.zahl(f.lochkreis));
      if (istZahl(f.ml)) setze(this, 'ml', 'ML ' + this.zahl(f.ml));
      var titel = this.querySelector('[data-nwf-reifen-titel]');
      if (titel && label && this.t.reifen_titel) titel.textContent = this.text('reifen_titel', { felge: label });
    }

    /* ------------------------------------------------------------- Reifen */
    zeigeReifen(r) {
      var karte = this.querySelector('[data-nwf-reifen]');
      if (!karte || !r || typeof r !== 'object') return;
      if (r.status !== 'ok') {
        // Keine PKW-Felge, keine Norm fuer diese Maulweite, keine lieferbare Groesse:
        // keine Reifenzeile (Plan 4.5). 'retry' laesst die Umleitung stehen.
        if (r.status === 'keine-pkw-felge' || r.status === 'ohne-mass' || r.status === 'keine-groesse') karte.hidden = true;
        return;
      }
      var groessen = (Array.isArray(r.groessen) ? r.groessen : []).filter(function (g) {
        return g && istZahl(g.breite) && istZahl(g.querschnitt) && istZahl(g.zoll) && g.anzahl && typeof g.anzahl === 'object';
      });
      if (!groessen.length) {
        karte.hidden = true;
        return;
      }
      this.groessen = groessen;
      this.reifenKarte = karte;
      // MUSTER (nur Vorschau): die Umleitung der App gibt es erst nach dem Promote (404) — in
      // der Abnahme fuehren Knopf und „andere Größe" deshalb auf das erste Ziel der Antwort.
      if (this.muster) {
        for (var i = 0; i < groessen.length; i++) {
          var u = groessen[i].ziel && groessen[i].ziel.url;
          if (typeof u === 'string' && ZIEL_RE.test(u)) {
            this.umleitung = u;
            break;
          }
        }
      }
      this.wahl = karte.querySelector('[data-nwf-wahl]');
      this.cta = karte.querySelector('[data-nwf-cta]');
      this.ctaText = karte.querySelector('[data-nwf-cta-text]');
      this.ctaGrund = this.ctaText ? this.ctaText.textContent : '';
      if (!this.wahl || !this.cta) return;

      this.baueBreiten();
      this.bindeSaison();
      this.zeigeAndere(Array.isArray(r.andere) ? r.andere : []);

      var norm = karte.querySelector('[data-nwf-normstand]');
      if (norm && typeof r.normstand === 'string' && r.normstand) {
        norm.textContent = this.text('reifen_grundlage', { norm: r.normstand });
        norm.hidden = false;
      }
      var aus = r.ausschluesse || {};
      var ohne = karte.querySelector('[data-nwf-reifen-ohne]');
      if (ohne && ((aus.runflat || 0) > 0 || (aus.cReifen || 0) > 0)) ohne.hidden = false;
      var winter = karte.querySelector('[data-nwf-winterfest]');
      if (winter && this.felge && this.felge.winterfest === false) winter.hidden = false;

      var link = karte.querySelector('[data-nwf-reifen-link]');
      if (link) link.hidden = true;
      this.wahl.hidden = false;
      this.zeigeErgebnis();
      this.komplettVerbinden();
    }

    /* KOMPAKT (Pruefrunde 1 Phase B, minor): steht „Reifen fuer diese Felge" (<ap-komplett-reifen>,
     * sections/wheel-tyre-link) bereit auf der Seite — mit DERSELBEN felgen-set-Antwort (Proxy: ein
     * Abruf je Seite; Muster: dieselbe Rechnung aus der Felge) —, wird die Reifenkarte hier eine
     * Uebersichtszeile mit Sprung dorthin. Groessen, Saison, Ab-Preise, „andere Groesse" und die
     * Reifenkarten stehen dort; nichts entfaellt. Wird der andere Block nie bereit, bleibt alles. */
    komplettVerbinden() {
      var self = this;
      var pruefe = function () {
        var kr = document.querySelector('ap-komplett-reifen[data-ap-kr-bereit]');
        if (!kr || kr.hidden || !kr.id) return false;
        self.kompakt(kr);
        return true;
      };
      if (pruefe()) return;
      var hoer = function () {
        if (pruefe()) document.removeEventListener('ap-kr:bereit', hoer);
      };
      document.addEventListener('ap-kr:bereit', hoer);
    }

    kompakt(kr) {
      var karte = this.reifenKarte;
      if (!karte || !this.groessen || karte.classList.contains('ap-nwf__karte--kompakt')) return;
      var zeile = karte.querySelector('[data-nwf-kompakt]');
      if (!zeile) return;
      var summe = this.groessen.reduce(function (acc, g) {
        return acc + (istZahl(g.anzahl.alle) ? g.anzahl.alle : 0);
      }, 0);
      var text = zeile.querySelector('[data-nwf-kompakt-text]');
      if (text) text.textContent = this.text('reifen_uebersicht', { g: this.groessen.length, n: this.anzahl(summe) });
      var sprung = zeile.querySelector('[data-nwf-kompakt-link]');
      if (sprung) sprung.setAttribute('href', '#' + kr.id);
      if (this.wahl) this.wahl.hidden = true;
      zeile.hidden = false;
      karte.classList.add('ap-nwf__karte--kompakt');
      this.hinweisEinklappen(karte);
    }

    /* Pruefrunde 2 Phase B (minor): im Kompaktmodus stand derselbe Rechtshinweis dreimal hintereinander —
     * hier der lange Absatz „Technisch montierbar nach ETRTO-Norm. Zulässig … (Aufkleber oder Anzeige)" mit
     * drei Punkten und der Grundlage (mobil ~700 px Text fuer eine Uebersichtszeile), direkt danach Einleitung
     * und Fuss von „Reifen für diese Felge". Jetzt: EIN kurzer Satz bleibt offen („… zulässig ist, was Ihre
     * Fahrzeugpapiere bzw. die Freigabe der Felge nennen"), Absatz, Punkte und Grundlage stehen unveraendert
     * in einem <details> „Was heißt technisch montierbar?". Es entfaellt nichts; ohne den anderen Block
     * (kein Kompaktmodus) bleibt alles offen wie bisher. */
    hinweisEinklappen(karte) {
      if (karte.querySelector('[data-nwf-mehr]')) return;
      var teile = [
        karte.querySelector('[data-nwf-reifen-hinweis]'),
        karte.querySelector('[data-nwf-reifen-punkte]'),
        karte.querySelector('[data-nwf-normstand]'),
      ].filter(Boolean);
      if (!teile.length || !this.t.reifen_mehr) return;
      var mehr = el('details', 'ap-nwf__mehr');
      mehr.setAttribute('data-nwf-mehr', '');
      mehr.appendChild(el('summary', 'ap-nwf__mehr-kopf', this.text('reifen_mehr')));
      teile[0].parentNode.insertBefore(mehr, teile[0]);
      if (this.t.reifen_kurz) {
        var kurz = el('p', 'ap-nwf__text ap-nwf__kurz', this.text('reifen_kurz'));
        kurz.setAttribute('data-nwf-reifen-kurz', '');
        mehr.parentNode.insertBefore(kurz, mehr);
      }
      teile.forEach(function (t) {
        mehr.appendChild(t);
      });
    }

    breiten() {
      var seen = {};
      var out = [];
      this.groessen.forEach(function (g) {
        if (!seen[g.breite]) {
          seen[g.breite] = true;
          out.push(g.breite);
        }
      });
      return out.sort(function (a, b) {
        return a - b;
      });
    }

    chip(name, wert, text) {
      var label = el('label', 'ap-chip ap-chip--select ap-nwf__chip');
      var input = document.createElement('input');
      input.type = 'radio';
      input.name = name;
      input.value = String(wert);
      label.appendChild(input);
      label.appendChild(el('span', 'ap-nwf__chip-wert', text));
      // Die Anzahl ist eine Lesehilfe fuer das Auge (dieselbe Zahl sagt das Ergebnis
      // darunter per aria-live an) — fuer Vorleseprogramme bleibt der Chip „175".
      var meta = el('span', 'ap-chip__meta ap-nwf__chip-meta');
      meta.setAttribute('data-nwf-chip-meta', '');
      meta.setAttribute('aria-hidden', 'true');
      label.appendChild(meta);
      return label;
    }

    baueBreiten() {
      var box = this.wahl.querySelector('[data-nwf-breiten]');
      if (!box) return;
      box.textContent = '';
      var self = this;
      this.breiten().forEach(function (b) {
        box.appendChild(self.chip('NwfBreite-' + self.sid, b, b));
      });
      box.addEventListener('change', function (e) {
        var t = e.target;
        if (!t || t.type !== 'radio') return;
        self.breite = Number(t.value);
        self.querschnitt = null;
        self.baueQuerschnitte();
        self.zeigeErgebnis();
      });
      this.zaehleChips();
    }

    baueQuerschnitte() {
      var feld = this.wahl.querySelector('[data-nwf-qs-feld]');
      var box = this.wahl.querySelector('[data-nwf-querschnitte]');
      if (!feld || !box) return;
      box.textContent = '';
      var self = this;
      var qs = this.groessen
        .filter(function (g) {
          return g.breite === self.breite;
        })
        .map(function (g) {
          return g.querschnitt;
        })
        .sort(function (a, b) {
          return a - b;
        });
      qs.forEach(function (q) {
        box.appendChild(self.chip('NwfQs-' + self.sid, q, q));
      });
      if (!box.dataset.gebunden) {
        box.dataset.gebunden = '1';
        box.addEventListener('change', function (e) {
          var t = e.target;
          if (!t || t.type !== 'radio') return;
          self.querschnitt = Number(t.value);
          self.zeigeErgebnis();
        });
      }
      // Nur EIN Querschnitt zu dieser Breite: dann gibt es nichts zu waehlen.
      if (qs.length === 1) {
        var einzig = box.querySelector('input');
        if (einzig) einzig.checked = true;
        this.querschnitt = qs[0];
      }
      feld.hidden = qs.length === 0;
      this.zaehleChips();
    }

    bindeSaison() {
      var self = this;
      var feld = this.wahl.querySelector('.ap-nwf__feld--saison');
      if (!feld || feld.dataset.gebunden) return;
      feld.dataset.gebunden = '1';
      feld.addEventListener('change', function (e) {
        var t = e.target;
        if (!t || t.type !== 'radio' || SAISONS.indexOf(t.value) === -1) return;
        self.saison = t.value;
        self.zaehleChips();
        self.zeigeErgebnis();
      });
    }

    gewaehlt() {
      var self = this;
      if (this.breite === null || this.querschnitt === null) return null;
      for (var i = 0; i < this.groessen.length; i++) {
        var g = this.groessen[i];
        if (g.breite === self.breite && g.querschnitt === self.querschnitt) return g;
      }
      return null;
    }

    n(g, saison) {
      var v = g && g.anzahl ? g.anzahl[saison || this.saison] : 0;
      return istZahl(v) ? v : 0;
    }

    /* Anzahlen an Saison-Schaltern und Chips — je nach Auswahl und Saison. */
    zaehleChips() {
      var self = this;
      var basis = this.groessen.filter(function (g) {
        return (self.breite === null || g.breite === self.breite) && (self.querschnitt === null || g.querschnitt === self.querschnitt);
      });
      SAISONS.forEach(function (s) {
        var ziel = self.wahl.querySelector('[data-nwf-anzahl="' + s + '"]');
        if (!ziel) return;
        var summe = basis.reduce(function (acc, g) {
          return acc + self.n(g, s);
        }, 0);
        ziel.textContent = self.anzahl(summe);
      });
      Array.prototype.forEach.call(this.wahl.querySelectorAll('[data-nwf-breiten] input'), function (input) {
        var b = Number(input.value);
        var summe = self.groessen.reduce(function (acc, g) {
          return acc + (g.breite === b ? self.n(g) : 0);
        }, 0);
        var meta = input.parentNode.querySelector('[data-nwf-chip-meta]');
        if (meta) meta.textContent = self.anzahl(summe);
      });
      Array.prototype.forEach.call(this.wahl.querySelectorAll('[data-nwf-querschnitte] input'), function (input) {
        var q = Number(input.value);
        var summe = self.groessen.reduce(function (acc, g) {
          return acc + (g.breite === self.breite && g.querschnitt === q ? self.n(g) : 0);
        }, 0);
        var meta = input.parentNode.querySelector('[data-nwf-chip-meta]');
        if (meta) meta.textContent = self.anzahl(summe);
      });
    }

    alterLabel(dotJahr) {
      var y = parseInt(dotJahr, 10);
      if (!dotJahr || !isFinite(y)) return this.text('preis_dot_ohne_jahr');
      var diff = this.jahr - y;
      if (diff < 0) return this.text('preis_dot_jahr', { jahr: y });
      if (diff <= 1) return this.text('preis_fabrikneu', { jahr: y });
      if (diff <= 5) return this.text('preis_neuware', { jahr: y });
      return this.text('preis_dotware', { jahr: y });
    }

    zeigeErgebnis() {
      var w = this.wahl;
      var g = this.gewaehlt();
      var groesse = w.querySelector('[data-nwf-groesse]');
      var treffer = w.querySelector('[data-nwf-treffer]');
      var preise = w.querySelector('[data-nwf-preise]');
      var fuss = w.querySelector('[data-nwf-preise-fuss]');
      var saisonHinweis = w.querySelector('[data-nwf-preise-saison]');
      var spanne = w.querySelector('[data-nwf-spanne]');
      var self = this;
      this.zielLauf++;

      if (groesse) groesse.classList.toggle('ap-nwf__groesse--frage', !g);
      if (!g) {
        var summe = this.groessen.reduce(function (acc, x) {
          return acc + self.n(x);
        }, 0);
        if (groesse) groesse.textContent = this.text('reifen_waehlen');
        if (treffer) treffer.textContent = this.text('reifen_uebersicht', { g: this.groessen.length, n: this.anzahl(summe) });
        if (preise) preise.hidden = true;
        if (fuss) fuss.hidden = true;
        if (spanne) spanne.hidden = true;
        this.setzeCta(this.umleitung, this.ctaGrund);
        return;
      }

      var name = g.breite + '/' + g.querschnitt + ' R' + this.zahl(g.zoll);
      if (groesse) groesse.textContent = name;
      if (treffer) treffer.textContent = this.text('reifen_anzahl', { n: this.anzahl(this.n(g)) });

      var ab = g.abPreis || {};
      var zeilen = 0;
      var zeile = function (art, betrag, label) {
        var z = preise ? preise.querySelector('[data-nwf-preis="' + art + '"]') : null;
        if (!z) return;
        if (!istZahl(betrag) || betrag <= 0) {
          z.hidden = true;
          return;
        }
        zeilen++;
        var l = z.querySelector('[data-nwf-preis-label]');
        if (l) l.textContent = label;
        var b = z.querySelector('[data-nwf-preis-betrag]');
        if (b) b.textContent = self.text('preis_ab', { preis: self.geld(betrag) });
        z.hidden = false;
      };
      zeile('neu', ab.neu, this.text('preis_ohne_dot'));
      zeile('dot', ab.dot, this.alterLabel(ab.dotJahr));
      if (preise) preise.hidden = zeilen === 0;
      if (fuss) fuss.hidden = zeilen === 0;
      if (saisonHinweis) saisonHinweis.hidden = this.saison === 'alle';
      if (spanne) {
        if (istZahl(g.jMin) && istZahl(g.jMax) && istZahl(g.messfelge)) {
          spanne.textContent = mitEinheit(this.text('reifen_spanne', { von: this.zahl(g.jMin), bis: this.zahl(g.jMax), mess: this.zahl(g.messfelge) }));
          spanne.hidden = false;
        } else spanne.hidden = true;
      }

      // Ziel: alle Saisons -> das Ziel der Antwort; je Saison fragt die Groessensuche
      // (scope felge). Der Knopftext nennt die Saison erst, wenn ihr Ziel feststeht.
      var alleZiel = g.ziel && typeof g.ziel.url === 'string' && ZIEL_RE.test(g.ziel.url) ? g.ziel.url : '';
      this.setzeCta(alleZiel || this.umleitung, this.text('reifen_cta', { groesse: name }));
      var saison = this.saison;
      if (saison === 'alle' && alleZiel) return;
      if (typeof window.apSizeTarget !== 'function') return;
      var lauf = this.zielLauf;
      var params = { kind: 'tyre', scope: 'felge', w: g.breite, a: g.querschnitt, r: g.zoll };
      if (saison !== 'alle') params.season = SAISON_VERTRAG[saison];
      Promise.resolve(window.apSizeTarget(params))
        .then(function (url) {
          if (lauf !== self.zielLauf || typeof url !== 'string' || !url) return;
          var text = saison === 'alle' ? self.text('reifen_cta', { groesse: name }) : self.text('reifen_cta_saison', { saison: self.t['saison_' + saison] || '', groesse: name });
          self.setzeCta(url, text);
        })
        .catch(function () {});
    }

    setzeCta(href, text) {
      if (!this.cta) return;
      if (href && this.cta.getAttribute('href') !== href) this.cta.setAttribute('href', href);
      if (this.ctaText && text && this.ctaText.textContent !== text) this.ctaText.textContent = text;
    }

    /* „Andere Größe": was im Bestand liegt, nach ETRTO aber nicht zu dieser Maulweite
     * gehoert — nie gesperrt, nie „nicht montierbar" (Gutachten-Konflikt 165/70 R14).
     * Die Ziele holt die Groessensuche erst beim Aufklappen (sparsam). */
    zeigeAndere(andere) {
      var box = this.reifenKarte.querySelector('[data-nwf-andere]');
      if (!box) return;
      var self = this;
      var zoll = this.felge && istZahl(this.felge.zoll) ? this.felge.zoll : this.groessen[0].zoll;
      var liste = andere.filter(function (x) {
        return x && istZahl(x.breite) && istZahl(x.querschnitt) && istZahl(x.anzahl) && x.anzahl > 0;
      });
      if (!liste.length) return;
      var ul = box.querySelector('[data-nwf-andere-liste]');
      var p = box.querySelector('[data-nwf-andere-text]');
      if (p && this.felge && istZahl(this.felge.j)) p.textContent = this.text('andere_text', { j: this.zahl(this.felge.j, true) });
      if (!ul) return;
      ul.textContent = '';
      liste.forEach(function (x) {
        var grund = '';
        if (x.grund === 'ausserhalb-j' && istZahl(x.jMin) && istZahl(x.jMax)) grund = self.text('andere_ausserhalb', { von: self.zahl(x.jMin), bis: self.zahl(x.jMax) });
        else if (x.grund === 'nur-b-kontur' && istZahl(x.bMin) && istZahl(x.bMax)) grund = self.text('andere_bkontur', { von: self.zahl(x.bMin), bis: self.zahl(x.bMax) });
        else if (x.grund === 'ohne-norm') grund = self.text('andere_ohne_norm');
        var li = el('li', 'ap-nwf__andere-eintrag');
        var a = el('a', 'ap-nwf__andere-link');
        a.setAttribute('href', self.umleitung || '#');
        a.dataset.w = String(x.breite);
        a.dataset.a = String(x.querschnitt);
        a.appendChild(el('span', 'ap-nwf__andere-groesse', x.breite + '/' + x.querschnitt + ' R' + self.zahl(zoll)));
        // Leerzeichen zwischen den Teilen: der Linkname liest sich „155/65 R14 ETRTO …",
        // nicht „155/65 R14ETRTO …" (im Spaltenlayout ohne Wirkung auf die Optik).
        a.appendChild(document.createTextNode(' '));
        var meta = mitEinheit([grund, self.text('reifen_anzahl', { n: self.anzahl(x.anzahl) })].filter(Boolean).join(' · '));
        a.appendChild(el('span', 'ap-nwf__andere-meta', meta));
        li.appendChild(a);
        ul.appendChild(li);
      });
      box.addEventListener('toggle', function () {
        if (!box.open || box.dataset.zieleGeholt || typeof window.apSizeTarget !== 'function') return;
        box.dataset.zieleGeholt = '1';
        Array.prototype.forEach.call(ul.querySelectorAll('a[data-w]'), function (a) {
          Promise.resolve(window.apSizeTarget({ kind: 'tyre', scope: 'felge', w: a.dataset.w, a: a.dataset.a, r: zoll }))
            .then(function (url) {
              if (typeof url === 'string' && url) a.setAttribute('href', url);
            })
            .catch(function () {});
        });
      });
      box.hidden = false;
    }

    /* -------------------------------------------------------- Zentrierring */
    kaufliste() {
      return this.bereich.querySelector('needed-with');
    }

    nwRefresh() {
      var nw = this.kaufliste();
      if (nw && typeof nw.refresh === 'function') nw.refresh();
    }

    /* Die App hat zum Ring entschieden (Auswahl, Luecke oder bewusst nichts, etwa bei
     * widerspruechlicher Bohrung): die bisherigen Ringzeilen gelten nicht mehr. */
    versteckeServerRinge() {
      var nw = this.kaufliste();
      if (!nw) return;
      Array.prototype.forEach.call(nw.querySelectorAll('[data-nw-ring]'), function (li) {
        var haken = li.querySelector('[data-needed-with-check]');
        if (haken) haken.checked = false;
        li.hidden = true;
      });
    }

    zeigeRing(ring) {
      var karte = this.querySelector('[data-nwf-ring]');
      if (!karte || !ring || typeof ring !== 'object' || !istZahl(ring.aussenMm)) return;
      this.ring = ring;
      this.versteckeServerRinge();
      var optionen = (Array.isArray(ring.optionen) ? ring.optionen : []).filter(function (o) {
        return o && istZahl(o.innenMm) && istZahl(o.variantId);
      });
      this.ringOptionen = optionen;
      if (!optionen.length && !ring.lueckensatz) {
        // Keine Auswahl und kein Lueckensatz (z. B. Bohrung laut Lieferant abweichend —
        // das sagt der Hinweis): keine Ringzeile.
        karte.hidden = true;
        this.nwRefresh();
        return;
      }
      var nenn = istZahl(ring.nennmassMm) ? ring.nennmassMm : ring.aussenMm;
      var aussen = karte.querySelector('[data-nwf-ring-aussen]');
      if (aussen) {
        aussen.textContent =
          Math.abs(nenn - ring.aussenMm) > 0.001
            ? this.text('ring_aussen', { aussen: this.zahl(ring.aussenMm), nenn: this.zahl(nenn) })
            : this.text('ring_aussen_gleich', { aussen: this.zahl(ring.aussenMm) });
      }
      var satz = karte.querySelector('[data-nwf-ring-nenn]');
      if (satz) {
        satz.textContent = this.text('ring_nennmass', { nenn: this.zahl(nenn) });
        satz.hidden = false;
      }
      var luecke = karte.querySelector('[data-nwf-ring-luecke]');
      if (luecke && ring.lueckensatz) {
        luecke.textContent = this.text('ring_luecke');
        luecke.hidden = false;
      }
      var feld = karte.querySelector('[data-nwf-ring-feld]');
      var select = karte.querySelector('[data-nwf-ring-select]');
      if (feld && select && optionen.length) {
        select.textContent = '';
        var leer = el('option', null, this.text('ring_option_leer'));
        leer.value = '';
        select.appendChild(leer);
        var self = this;
        optionen.forEach(function (o, i) {
          var kaufbar = o.kaufbar === true && istZahl(o.preis) && o.preis > 0;
          var opt = el('option', null, self.text(kaufbar ? 'ring_option' : 'ring_option_knapp', { innen: self.zahl(o.innenMm) }));
          opt.value = String(i);
          if (!kaufbar) opt.disabled = true;
          select.appendChild(opt);
        });
        var keiner = el('option', null, this.text('ring_option_keiner'));
        keiner.value = 'keiner';
        select.appendChild(keiner);
        select.addEventListener('change', function () {
          var o = /^\d+$/.test(select.value) ? optionen[Number(select.value)] : null;
          self.ringWahl(o || null);
        });
        feld.hidden = false;
      }
      karte.hidden = false;
      this.nwRefresh();
    }

    /* Eine KAUFZEILE fuer das gewaehlte Innenmass — nie angehakt; eine andere Wahl
     * ersetzt sie, „weiß ich nicht" entfernt sie. */
    ringWahl(o) {
      var nw = this.kaufliste();
      if (!nw) return;
      var alt = nw.querySelector('[data-nwf-ring-kauf]');
      if (alt && alt.parentNode) alt.parentNode.removeChild(alt);
      if (o && o.kaufbar === true && istZahl(o.preis) && o.preis > 0) {
        var liste = nw.querySelector('.ap-needed-with__list');
        if (liste) {
          var zeile = this.ringZeile(o);
          var zeilen = liste.querySelectorAll('[data-needed-with-item]');
          var letzte = zeilen.length ? zeilen[zeilen.length - 1] : null;
          if (letzte && letzte.nextSibling) liste.insertBefore(zeile, letzte.nextSibling);
          else if (letzte) liste.appendChild(zeile);
          else liste.insertBefore(zeile, liste.firstChild);
        }
      }
      this.nwRefresh();
    }

    ringZeile(o) {
      var titel = this.text('ring_kauf', { aussen: this.zahl(this.ring.aussenMm), innen: this.zahl(o.innenMm) });
      var li = el('li', 'ap-needed-with__item ap-needed-with__item--ring ap-needed-with__item--neu');
      li.setAttribute('data-needed-with-item', '');
      li.setAttribute('data-nwf-ring-kauf', '');
      li.dataset.variantId = String(o.variantId);
      li.dataset.price = String(Math.round(o.preis * 100));
      li.dataset.qty = '1';
      li.dataset.qtyMode = 'per-wheel';
      if (istZahl(o.bestand)) li.dataset.available = String(Math.max(0, Math.floor(o.bestand)));
      if (this.muster) li.setAttribute('data-muster', '');

      var id = 'NwfRingKauf-' + this.sid;
      var check = el('label', 'ap-needed-with__check');
      check.setAttribute('for', id);
      var input = document.createElement('input');
      input.type = 'checkbox';
      input.id = id;
      input.setAttribute('data-needed-with-check', '');
      input.setAttribute('aria-label', this.text('kauf_aria', { title: titel }));
      check.appendChild(input);
      li.appendChild(check);

      var media = el('span', 'ap-needed-with__media ap-needed-with__media--icon');
      media.setAttribute('aria-hidden', 'true');
      var icon = this.querySelector('template[data-nwf-icon-ring]');
      var svg = icon && icon.content ? icon.content.querySelector('svg') : null;
      if (svg) media.appendChild(svg.cloneNode(true));
      li.appendChild(media);

      var info = el('span', 'ap-needed-with__info');
      info.appendChild(el('span', 'ap-needed-with__title', titel));
      info.appendChild(el('span', 'ap-needed-with__ringhint', this.text('ring_kauf_hinweis')));
      if (this.muster) info.appendChild(el('span', 'ap-badge ap-badge--warning ap-nwf__muster-tag', this.text('muster_zeile')));
      li.appendChild(info);

      li.appendChild(el('span', 'ap-needed-with__price', this.geld(o.preis)));
      var vat = this.querySelector('template[data-nwf-vat]');
      if (vat && vat.content) li.appendChild(vat.content.cloneNode(true));
      return li;
    }

    /* ------------------------------------------------------------ Hinweise */
    zeigeBund() {
      var ziel = this.bereich.querySelector('[data-nwf-bef-bund]');
      var b = this.felge && this.felge.bund;
      if (!ziel || !b || typeof b !== 'object') return;
      var text = '';
      if (b.art === 'kugel') text = istZahl(b.radius) ? this.text('bef_bund_kugel_r', { radius: b.radius }) : this.text('bef_bund_kugel');
      else if (b.art === 'kegel') text = istZahl(b.winkel) ? this.text('bef_bund_kegel_w', { winkel: b.winkel }) : this.text('bef_bund_kegel');
      else if (b.art === 'flach') text = this.text('bef_bund_flach');
      if (!text) return;
      ziel.textContent = text;
      ziel.hidden = false;
    }

    /* Befestigung aus den Lieferantendaten (Fahrzeugdaten Felgen, Konzept-Stufe 1, 29.09.2026):
     * Die Plakette steht serverseitig neutral auf „Je nach Lieferant". Nur die App entscheidet
     * je Felge (felgen-set v1, additiv) — das Theme rechnet nichts:
     *   hinweise[].code 'ohne-befestigung' (quelle lieferant) -> „Nicht im Lieferumfang (laut
     *     Lieferant)" + „Laut Lieferant ohne Befestigungsteile und Nabendeckel."
     *   befestigung.beigabe 'mit-fahrzeug' -> „Mit Fahrzeugangabe dabei (laut Lieferant)" — die App
     *     setzt das NUR, wenn ALLE Bezugsquellen der Felge die Beigabe schriftlich bestaetigt haben
     *     (Einstellung, Standard leer). „ohne" sticht „dabei" (die vorsichtigere Aussage).
     *   hinweise[].code 'serien-befestigung' (text: Lieferantenwortlaut, von der App geprueft,
     *     hoechstens 80 Zeichen) -> „Laut Lieferant für Serien-Radschrauben bzw. -muttern
     *     ausgelegt[: text]."
     * Beide Codes stehen NUR hier, nicht noch einmal unter „Hinweise zu dieser Felge". */
    zeigeBefestigung(liste) {
      var karte = this.bereich.querySelector('[data-nwf-bef]');
      if (!karte) return;
      var lieferant = function (h) {
        return h && typeof h === 'object' && h.quelle === 'lieferant';
      };
      var ohne = liste.some(function (h) {
        return lieferant(h) && h.code === 'ohne-befestigung';
      });
      var serien = null;
      liste.forEach(function (h) {
        if (!serien && lieferant(h) && h.code === 'serien-befestigung') serien = h;
      });
      var dabei = !ohne && !!this.befestigung && this.befestigung.beigabe === 'mit-fahrzeug';
      // „ohne": der allgemeine Satz („Je nach Lieferant liegen … bei") widerspraeche der Plakette — an
      // seine Stelle tritt der serverseitig mitgegebene (data-ohne, mit bzw. ohne Fahrzeugschein-Eingabe).
      // „dabei" (Pruefrunde 01.10.2026): ebenso — „nicht fest im Lieferumfang … zusagen können wir die
      // Beigabe nicht" stuende sonst neben „Mit Fahrzeugangabe dabei (laut Lieferant)" (data-dabei).
      var satz = karte.querySelector('[data-nwf-bef-text]');
      var satzOhne = satz ? satz.getAttribute('data-ohne') : '';
      var satzDabei = satz ? satz.getAttribute('data-dabei') : '';
      if (ohne && satz && satzOhne) satz.textContent = satzOhne;
      else if (dabei && satz && satzDabei) satz.textContent = satzDabei;
      var badge = karte.querySelector('[data-nwf-bef-badge]');
      if (badge && (ohne || dabei)) {
        var text = ohne ? this.text('bef_badge_ohne') : this.text('bef_badge_dabei');
        if (text) {
          badge.textContent = text;
          badge.classList.remove('ap-badge--neutral');
          badge.classList.add(ohne ? 'ap-badge--warning' : 'ap-badge--success');
        }
      }
      var ul = karte.querySelector('[data-nwf-bef-lieferant]');
      if (!ul) return;
      ul.textContent = '';
      var vorlage = this.querySelector('template[data-nwf-icon-lieferant]');
      var zeile = function (satz) {
        if (!satz) return;
        var li = el('li', 'ap-nwf__bef-lieferant');
        if (vorlage && vorlage.content && vorlage.content.firstElementChild) li.appendChild(vorlage.content.firstElementChild.cloneNode(true));
        li.appendChild(el('span', '', satz));
        ul.appendChild(li);
      };
      if (ohne) zeile(this.text('bef_text_ohne'));
      else if (dabei) zeile(this.text('bef_text_dabei'));
      if (serien) {
        var roh = typeof serien.text === 'string' ? serien.text.trim() : '';
        zeile(roh && roh.length <= 80 ? this.text('bef_serien_text', { text: roh }) : this.text('bef_serien'));
      }
      ul.hidden = ul.children.length === 0;
    }

    hinweisText(h) {
      var mm = istZahl(h.mm) ? this.zahl(h.mm) : '';
      switch (h.code) {
        case 'achse':
          return h.achse === 'hinten' ? this.text('h_achse_hinten') : h.achse === 'vorn' ? this.text('h_achse_vorn') : '';
        case 'festigkeitsgutachten':
          return this.text('h_festigkeit');
        case 'exklusiv':
          // Die App prueft die Marken (Zeichensatz, keine Adresse, kein Freigabe-Wort,
          // hoechstens 80 Zeichen); hier nur noch die Laenge als Netz.
          return typeof h.marken === 'string' && h.marken.trim() && h.marken.length <= 80 ? this.text('h_exklusiv', { marken: h.marken.trim() }) : '';
        case 'laufrichtung':
          return h.seite === 'links' ? this.text('h_laufrichtung_links') : h.seite === 'rechts' ? this.text('h_laufrichtung_rechts') : '';
        case 'nicht-winterfest':
          return this.text('h_nicht_winterfest');
        case 'b-horn':
          return this.text('h_b_horn');
        case 'distanzscheibe':
          return mm ? this.text('h_distanz', { mm: mm }) : this.text('h_distanz_ohne');
        case 'big-cap':
          return this.text('h_big_cap');
        case 'fix-gebohrt':
          return mm ? this.text('h_fix', { mm: mm }) : '';
        case 'bohrung-abweichend':
          return mm ? this.text('h_bohrung_abweichend', { mm: mm }) : this.text('h_bohrung_abweichend_ohne');
        case 'bohrung-bearbeiten':
          return this.text('h_bohrung_bearbeiten');
        default:
          return '';
      }
    }

    zeigeHinweise(liste) {
      var karte = this.bereich.querySelector('[data-nwf-hinweise]');
      var ul = karte ? karte.querySelector('[data-nwf-hinweise-liste]') : null;
      if (!ul) return;
      ul.textContent = '';
      var self = this;
      liste.forEach(function (h) {
        if (!h || typeof h !== 'object') return;
        var text = self.hinweisText(h);
        if (!text) return;
        var li = el('li', 'ap-nwf__punkt');
        li.appendChild(el('span', 'ap-nwf__punkt-text', text));
        if (h.quelle === 'lieferant') {
          li.appendChild(document.createTextNode(' '));
          li.appendChild(el('span', 'ap-nwf__quelle-tag', self.text('quelle_lieferant')));
        }
        ul.appendChild(li);
      });
      karte.hidden = ul.children.length === 0;
    }

    /* Mengen der Hinweise folgen der Buybox (Plan 2.5): Lochzahl x Felgen, ein Sensor je
     * Rad. Ohne Mengenfeld bleibt der Satz aus dem Liquid (vier Felgen). */
    zeigeMengen() {
      var feld = mengenFeld();
      var wert = feld ? parseInt(feld.value, 10) : NaN;
      if (!(wert > 0)) return;
      var felgen = wert;
      var loch = this.befestigung ? this.befestigung.jeFelge : this.loch;
      var bef = this.bereich.querySelector('[data-nwf-bef-menge]');
      if (bef && loch >= 3 && loch <= 12) {
        var n = loch * felgen;
        var t = felgen === 1 ? this.text('bef_menge_eins', { loch: loch, n: n }) : this.text('bef_menge', { felgen: felgen, loch: loch, n: n });
        if (t && bef.textContent !== t) bef.textContent = t;
        if (t) bef.hidden = false;
      }
      var rdks = this.bereich.querySelector('[data-nwf-rdks-menge]');
      if (rdks) {
        var r = felgen === 1 ? this.text('rdks_menge_eins') : this.text('rdks_menge', { n: felgen });
        if (r && rdks.textContent !== r) rdks.textContent = r;
      }
    }

    onMenge(e) {
      var t = e && e.target;
      if (!t || t.name !== 'quantity') return;
      if (t !== mengenFeld()) return;
      this.zeigeMengen();
    }
  }

  function definiere() {
    if (!customElements.get('ap-felgen-set')) customElements.define('ap-felgen-set', ApFelgenSet);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', definiere, { once: true });
  } else {
    definiere();
  }
})();
